import { useCallback, useEffect, useRef, useState } from "react"
import { get } from "helpers/api_helper"
import { setLuckyDraw, setMainDraw, updateLuckyDraw } from "../../../../../api"
import { invalidateEventCache } from "../../../../../eventCachePolicy"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { RESULTS_LOSS, drawFeedbackCopy, drawFailureMessage, drawFailureReason, resultsLossMessage } from "./drawFeedbackCopy"

const actions = {
  initial: { submit: setLuckyDraw, audit: "redraw", topic: "events-response", success: "SUCCESSFULLY_COMPETITION_DRAW", pending: "Generating Lucky Draw… Waiting for confirmation.", done: "Lucky Draw generated successfully." },
  swap: { submit: updateLuckyDraw, audit: "swap", topic: "events-response", success: "SUCCESSFULLY_UPDATED_COMPETITION_DRAW", pending: "Swapping positions… Waiting for confirmation.", done: "Positions swapped successfully." },
  redraw: { submit: setLuckyDraw, audit: "redraw", topic: "events-response", success: "SUCCESSFULLY_COMPETITION_DRAW", pending: "Redrawing positions… Waiting for confirmation.", done: "Lucky Draw redrawn successfully." },
  generate: { submit: setMainDraw, audit: "generate", topic: "matches-response", success: "SUCCESSFULLY_CREATED_MATCH", pending: "Generating Main Draw… Waiting for confirmation.", done: "Main Draw generated successfully." },
}
const stop = request => { clearTimeout(request?.timer); clearTimeout(request?.poll) }
const GATEWAY_UNAVAILABLE = "SERVICE_UNAVAILABLE"
// The live check: after this window a check reads once per click, and the operator may unlock (P120).
const CHECK_WINDOW_MS = 60000
const windowEnded = request => Date.now() - request.startedAt >= CHECK_WINDOW_MS
// What Action Logs hold for this request, when no terminal record of it settles the operation.
const auditFinding = (data, isThisOperation) => {
  if (!Number.isSafeInteger(data?.total) || !Array.isArray(data.logs)) return { kind: "unavailable" }
  if (data.total === 0) return { kind: "none" }
  if (data.total === 1) return data.logs[0] && isThisOperation(data.logs[0])
    ? { kind: "recorded", outcome: data.logs[0].outcome, since: data.logs[0].initiatedAt } : { kind: "other" }
  return { kind: "multiple", total: data.total, outcomes: data.logs.map(log => log.outcome) }
}

export default function useDrawOperation({ eventId, competitionId, userId, socket, socketClientId, refresh, addToast, enabled = true }) {
  const active = useRef(null)
  const ownedCorrelation = useRef(null)
  const [state, setState] = useState({})
  const callbacks = useRef({})
  callbacks.current = { refresh, addToast }
  const storageKey = userId && eventId && competitionId ? `sportya:draw:${userId}:${eventId}:${competitionId}` : null
  const refreshData = useCallback(() => {
    invalidateEventCache({ eventId, competitionId })
    callbacks.current.refresh?.(eventId)
  }, [eventId, competitionId])
  const save = useCallback(request => {
    if (!storageKey) return
    try { sessionStorage.setItem(storageKey, JSON.stringify({ action: request.action, operationId: request.operationId,
      correlationId: request.correlationId, phaseId: request.phaseId, startedAt: request.startedAt, answer: request.answer })) }
    catch { /* The original stable operation identity still permits read-only recovery. */ }
  }, [storageKey])
  const uncertain = useCallback(request => {
    if (active.current !== request) return
    stop(request)
    setState({ action: request.action, phaseId: request.phaseId, status: "unknown", checking: Boolean(request.checking), checkResult: request.checkResult,
      startedAt: request.startedAt, windowEnded: Boolean(request.findings), findings: request.findings, message: drawFeedbackCopy(request.action).unknown })
  }, [])
  const finish = useCallback((request, confirmed, failure, data) => {
    if (active.current !== request) return
    stop(request)
    active.current = null
    if (storageKey) {
      try { sessionStorage.removeItem(storageKey) } catch { /* A retained receipt can still be checked on reload. */ }
    }
    // P51 point 5: results would be lost — nothing changed yet; the same action goes again only on an explicit confirmation.
    if (!confirmed && failure === RESULTS_LOSS) {
      const count = Number(data?.results)
      setState({ action: request.action, phaseId: request.phaseId, status: "confirm", details: request.details,
        results: Number.isSafeInteger(count) && count > 0 ? count : undefined, message: resultsLossMessage(request.action, count) })
      return
    }
    const message = confirmed ? actions[request.action].done : drawFailureMessage(request.action, failure)
    setState({ action: request.action, phaseId: request.phaseId, status: confirmed ? "saved" : "error", message })
    callbacks.current.addToast?.(message, { appearance: confirmed ? "success" : "error", autoDismiss: true })
    refreshData()
  }, [refreshData, storageKey])
  // The matches of the request's own phase (the primary draw has none) now in the Main Draw, or "unavailable".
  const mainDrawMatches = useCallback(async request => {
    try {
      const response = await get(`/matches/${competitionId}`, { timeout: 15000 })
      if (!Array.isArray(response?.data)) return "unavailable"
      return response.data.filter(match => String(match?.competition?.phaseId || "") === String(request.phaseId || "")).length
    } catch { return "unavailable" }
  }, [competitionId])
  const check = useCallback(async request => {
    if (!request || active.current !== request || request.checking) return
    clearTimeout(request.poll)
    request.checking = true
    setState(previous => ({ ...previous, checking: true }))
    let checkResult = "unconfirmed"
    let logs = { kind: "unavailable" }
    // Never infer completion from another operation, phase, or an existing draw.
    // The edge proxy may replace transport correlation. The scoped operation
    // identity survives it; multiple audit attempts must remain ambiguous.
    const isThisOperation = log => log.operationId === request.operationId
      && log.eventId === eventId && log.competitionId === competitionId
      && log.action === actions[request.action].audit && (log.phaseId || "") === (request.phaseId || "")
    try {
      const response = await get(`/admin/draw-logs/${competitionId}`, { timeout: 15000, params: {
        eventId, operationId: request.operationId, limit: 2,
      } })
      if (active.current !== request) { request.checking = false; return }
      const data = response?.data
      const log = data?.total === 1 && data.logs?.length === 1 && data.logs[0]
      if (log && isThisOperation(log) && (log.outcome === "confirmed" || log.outcome === "failed")) {
        request.checking = false
        // A failed log names its cause in `reason` (a code); older logs have none and keep the generic advice.
        finish(request, log.outcome === "confirmed", log.reason)
        return
      }
      logs = auditFinding(data, isThisOperation)
    } catch { checkResult = "unavailable" } // A failed read does not prove the draw failed.
    // P120: once the window has passed, show the operator what CP found, including the Main Draw a generation would have made.
    if (windowEnded(request)) request.findings = { answer: request.answer, logs, matches: request.action === "generate" ? await mainDrawMatches(request) : undefined }
    request.checking = false
    if (active.current !== request) return
    request.checkResult = checkResult
    setState(previous => ({ ...previous, checking: false, checkResult, windowEnded: Boolean(request.findings), findings: request.findings }))
    if (active.current === request && !windowEnded(request)) {
      request.poll = setTimeout(() => check(request), 2000)
    } else uncertain(request)
  }, [competitionId, eventId, finish, uncertain, mainDrawMatches])
  useEffect(() => {
    if (!enabled) return
    setState({})
    ownedCorrelation.current = null
    if (storageKey) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey) || "null")
        if (saved) {
          if (!Object.prototype.hasOwnProperty.call(actions, saved.action) || typeof saved.operationId !== "string"
            || typeof saved.correlationId !== "string" || !Number.isFinite(saved.startedAt)
            || (saved.answer !== undefined && typeof saved.answer !== "string")) throw new Error("Invalid receipt")
          const request = { ...saved }
          active.current = request
          ownedCorrelation.current = request.correlationId
          setState({ action: request.action, phaseId: request.phaseId, status: "unknown", startedAt: request.startedAt, message: drawFeedbackCopy(request.action).unknown })
          check(request)
          request.timer = setTimeout(() => { uncertain(request); check(request) }, Math.max(0, CHECK_WINDOW_MS - (Date.now() - request.startedAt)))
        }
      } catch {
        // P120: the receipt can be unlocked like an unconfirmed operation; it is never read again.
        setState({ status: "blocked", blockedBy: "receipt", message: "The saved draw operation could not be read. Check Action Logs before generating another draw." })
      }
    }
    const receive = (topic, result) => {
      const request = active.current
      if (!request || result.correlationId !== request.correlationId
        || (result.operationId && result.operationId !== request.operationId)
        || (result.eventId && result.eventId !== eventId) || (result.competitionId && result.competitionId !== competitionId)) return
      const definition = actions[request.action]
      if (topic !== definition.topic && !(request.action === "generate" && topic === "events-response" && result.success === false)) return
      if (result.outcome === "unknown") { uncertain(request); check(request); return }
      if (result.success !== false && !(result.success === true && result.message === definition.success)) return
      finish(request, result.success === true, result.message, result.data)
    }
    const events = result => receive("events-response", result)
    const matches = result => receive("matches-response", result)
    const disconnect = () => { if (active.current) { uncertain(active.current); check(active.current) } }
    const reconnect = () => check(active.current)
    socket?.on("events-response", events)
    socket?.on("matches-response", matches)
    socket?.on("disconnect", disconnect)
    socket?.on("connect", reconnect)
    return () => {
      stop(active.current)
      active.current = null
      socket?.off("events-response", events)
      socket?.off("matches-response", matches)
      socket?.off("disconnect", disconnect)
      socket?.off("connect", reconnect)
    }
  }, [enabled, storageKey, eventId, competitionId, socket, check, finish, uncertain])
  const run = async (action, details = {}) => {
    if (!enabled || active.current || state.status === "blocked") return
    const definition = actions[action]
    const operationId = newPlayerOperationId()
    const request = { action, details, operationId, correlationId: operationId, phaseId: details.phaseId || "", startedAt: Date.now() }
    if (storageKey) {
      try { sessionStorage.setItem(storageKey, JSON.stringify(request)) }
      catch {
        setState({ status: "blocked", message: "The draw request could not be saved in this browser. Enable session storage and reload before generating." })
        return
      }
    }
    active.current = request
    ownedCorrelation.current = request.correlationId
    setState({ action, phaseId: request.phaseId, status: "pending", message: definition.pending })
    request.timer = setTimeout(() => { uncertain(request); check(request) }, CHECK_WINDOW_MS)
    request.poll = setTimeout(() => check(request), 2000)
    try {
      const result = await definition.submit({ eventId, competitionId, socketClientId, ...details,
        operationId, correlationId: request.correlationId })
      if (active.current !== request) return // A socket result can precede HTTP 202.
      const acknowledgement = result?.data?.message ? result.data : result
      if (acknowledgement?.message === "REQUEST_SENT" && acknowledgement.operationId === request.operationId
        && /^[a-zA-Z0-9-]{16,128}$/.test(acknowledgement.correlationId || "")) {
        request.correlationId = acknowledgement.correlationId
        ownedCorrelation.current = request.correlationId
      }
      const sent = result?.message === "REQUEST_SENT" || result?.data?.message === "REQUEST_SENT"
      // The server's answer is kept with the receipt: after the window it tells the operator whether the request got through.
      request.answer = sent ? "accepted" : "unexpected"
      save(request)
      if (!sent) { uncertain(request); check(request) }
    } catch (error) {
      if (active.current !== request) return
      const status = error?.response?.status
      const code = error?.response?.data?.message
      if (status >= 400 && status < 500 && status !== 408) {
        // The gateway refused the send, so nothing reached Events: name a known code, otherwise say it was rejected.
        finish(request, false, status === 401 ? "SESSION_EXPIRED" : drawFailureReason(code) ? code : "REQUEST_REJECTED", error?.response?.data?.data)
      } else if (status === 503 && code === GATEWAY_UNAVAILABLE) {
        // P120: on a command route the gateway answers this code only while it checks the session and the account, before it
        // publishes anything. An edge 503 carries an HTML page instead and stays uncertain.
        finish(request, false, GATEWAY_UNAVAILABLE)
      } else {
        request.answer = status ? `error:${status}` : "none"
        save(request)
        uncertain(request); check(request)
      }
    }
  }
  // P120: the operator's way out of an operation that cannot be confirmed. It forgets the operation in this tab only and
  // repeats nothing; a later answer is ignored here and stays in Action Logs.
  const canUnlock = (state.status === "unknown" && Boolean(state.windowEnded)) || (state.status === "blocked" && state.blockedBy === "receipt")
  const unlock = () => {
    if (!canUnlock) return
    stop(active.current)
    active.current = null
    if (storageKey) {
      try { sessionStorage.removeItem(storageKey) } catch { /* A receipt that cannot be removed is offered again on reload. */ }
    }
    setState({ action: state.action, phaseId: state.phaseId, status: "unlocked", message: "Draw changes unlocked" })
    refreshData()
  }
  return { ...state, eventId, competitionId, locked: ["pending", "unknown", "blocked"].includes(state.status), run,
    confirmResultsLoss: () => state.status === "confirm" && run(state.action, { ...state.details, confirmResultsLoss: true }),
    dismiss: () => state.status === "confirm" && setState({}),
    owns: result => Boolean(result.correlationId && result.correlationId === ownedCorrelation.current),
    recover: () => check(active.current), refresh: refreshData, canUnlock, unlock }
}
